import { cn, currencyFormatter } from '@/lib/utils'
import { Alert, AlertDescription, AlertTitle } from '@/shad/ui/alert'
import { Users } from 'lucide-react'
import { useState } from 'react'
import type { Purse } from '@/queries/program_purses/schemas.ts'
import FormButtons from '../_components/form-buttons'
import {
  AwardFormSteps,
  ManualAward,
  StudentAwardInformation,
} from '../award-form'
import ManualAwardFundedStudentWarning from './_components/manual-award-funded-student-warning'
import ManualAwardStep from './_components/manually-award-students'
import { StudentCard } from './_components/student-card'

interface SelectStudentsStepFormProps {
  selectedStudents: StudentAwardInformation[]
  purse: Purse
  onNextClick: () => void
  onBackClick: () => void
  onCancelClick: () => void
  onManualAwardChange: (studentsManualAward: ManualAward[number]) => void
  onManualAwardRemove: (studentId: number) => void
  currentStep: AwardFormSteps
  onStepChange: (step: AwardFormSteps) => void
  isAwardPreviewFetching: boolean
}

export function SelectStudentsStepForm({
  selectedStudents,
  purse,
  onNextClick,
  onBackClick,
  onCancelClick,
  currentStep,
  onStepChange,
  onManualAwardChange,
  onManualAwardRemove,
  isAwardPreviewFetching,
}: SelectStudentsStepFormProps) {
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null)
  const [selectedStudentIdForManualAward, setSelectedStudentIdForManualAward] =
    useState<number | null>(null)
  const totalLeftInPurse = purse.balance_available

  const totalToBeDistributed = selectedStudents.reduce((acc, student) => {
    return student.amount_to_be_awarded + acc
  }, 0)

  function hasEnoughToCoverAllStudents() {
    return totalToBeDistributed <= totalLeftInPurse
  }

  function handleRemoveManualAward(studentId: number) {
    onManualAwardRemove(studentId)
  }

  function handleManualAwardSave(amount: number, reason: string) {
    onManualAwardChange({
      studentId: selectedStudentIdForManualAward!,
      manualAwardAmount: amount,
      manualAwardReason: reason,
    })
    setSelectedStudentIdForManualAward(0)
    onStepChange('review_students')
  }

  if (currentStep === 'student_recieveing_additional_award_warning') {
    return (
      <ManualAwardFundedStudentWarning
        selectedStudents={selectedStudents}
        onProceed={() => onStepChange('confirm_award')}
        onCancel={() => onCancelClick()}
      />
    )
  }

  if (currentStep === 'award-student-manually') {
    const selectedStudent = selectedStudents.find(
      (student) => student.id === selectedStudentIdForManualAward
    )

    if (!selectedStudent) {
      throw new Error('Selected student not found')
    }

    const initialAmount = selectedStudent.amount_to_be_awarded
    const initialReason = selectedStudent.manual_awarded_amount_reason

    return (
      <ManualAwardStep
        totalLeftInPurse={totalLeftInPurse}
        totalToBeAwarded={totalToBeDistributed}
        onCancel={() => onStepChange('review_students')}
        onSave={handleManualAwardSave}
        student={selectedStudent}
        initialAmount={initialAmount}
        initialReason={initialReason || ''}
      />
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Selected Students
            </h2>
            <p className="text-sm text-gray-600">
              {selectedStudents.length} student
              {selectedStudents.length !== 1 ? 's' : ''} will receive
              scholarships
            </p>
          </div>
        </div>

        <div className="">
          <div className="overflow-y-auto">
            {selectedStudents.map((student) => {
              return (
                <StudentCard
                  key={student.id}
                  student={student}
                  isManuallyAwarded={student.isManuallyAwarded}
                  openPopoverId={openPopoverId}
                  setOpenPopoverId={setOpenPopoverId}
                  onManualAwardClick={(studentId) => {
                    onStepChange('award-student-manually')
                    setSelectedStudentIdForManualAward(studentId)
                  }}
                  onRemoveManualAward={handleRemoveManualAward}
                />
              )
            })}
          </div>

          <div className=" py-6 border-t border-gray-200 rounded-b-xl">
            <div className="text-sm space-y-1">
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-800 font-medium text-sm">
                  Number of Students to be Awarded
                </span>
                <span className="font-medium text-gray-800">
                  {selectedStudents.length}
                </span>
              </div>
              <div className="flex w-full items-center justify-between gap-4">
                <span className="text-gray-800 font-medium text-sm">
                  Total Award Amount
                </span>
                <span className="font-medium text-gray-800">
                  {currencyFormatter.format(totalToBeDistributed)}
                </span>
              </div>
              <div className="flex w-full items-center justify-between gap-4">
                <span className="text-gray-800 font-medium text-sm">
                  Purse Balance
                </span>
                <span className={cn('font-medium text-gray-800')}>
                  {currencyFormatter.format(totalLeftInPurse)}
                </span>
              </div>
              <div className="flex w-full items-center justify-between gap-4">
                <span className="text-gray-800 font-medium text-sm">
                  Remaining in Purse after awards
                </span>
                <span
                  className={cn(
                    'font-medium text-gray-800',
                    !hasEnoughToCoverAllStudents() && 'text-danger'
                  )}
                >
                  {currencyFormatter.format(
                    totalLeftInPurse - totalToBeDistributed
                  )}
                </span>
              </div>
            </div>
          </div>
          {!hasEnoughToCoverAllStudents() && (
            <div className=" py-2">
              <Alert variant={'destructive'}>
                <AlertTitle className="text-sm">Not enough funds</AlertTitle>
                <AlertDescription>
                  The total award amount is greater than the remaining purse
                  balance.
                </AlertDescription>
              </Alert>
            </div>
          )}
          <FormButtons>
            <FormButtons.BackButton onClick={onBackClick} />
            <FormButtons.NextButton
              disabled={
                !hasEnoughToCoverAllStudents() || isAwardPreviewFetching
              }
              onClick={onNextClick}
            />
          </FormButtons>
        </div>
      </div>
    </div>
  )
}
